/**
 * Single source of truth for VS Code extension manifest fetching and URL handling
 */

// URL constants - defined once as prefixes
const RELEASES_GITPOD_IO = 'https://releases.gitpod.io/';

// Compute proxy URL based on current hostname
// For vscode.gitpod.io -> app.gitpod.io/releases
// For vscode.custom.domain -> custom.domain/releases
function computeReleasesProxy(): string {
    if (typeof window === 'undefined' || !window.location) {
        return 'https://app.gitpod.io/releases/';
    }
    const hostname = window.location.hostname;
    if (hostname === 'vscode.gitpod.io') {
        return 'https://app.gitpod.io/releases/';
    }
    if (hostname.startsWith('vscode.')) {
        return `https://${hostname.substring('vscode.'.length)}/releases/`;
    }
    return 'https://app.gitpod.io/releases/';
}

const RELEASES_PROXY = computeReleasesProxy();
const PRIMARY_TIMEOUT_MS = 1000; // 1 second timeout for primary URL

export interface GitpodVSCodeExtensionManifest {
    version: string;
    release_date: string;
    vscode_web_extension_url: string;
}

export interface FetchManifestResult {
    extensionUrl: string;
}

// Track which base URL was successful - defaults to preferred (proxy)
let useProxy = true;

/**
 * Validates manifest and extracts extension URL
 */
export function validateAndExtractExtensionUrl(manifest: GitpodVSCodeExtensionManifest): string {
    if (!manifest.vscode_web_extension_url) {
        throw new Error('Missing vscode_web_extension_url in manifest');
    }
    // Validate URL format
    new URL(manifest.vscode_web_extension_url);
    return `${manifest.vscode_web_extension_url}/extension`;
}

/**
 * Fetches manifest with timeout
 */
async function fetchWithTimeout(url: string, timeoutMs: number): Promise<Response> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    
    try {
        const response = await fetch(url, { signal: controller.signal });
        clearTimeout(timeoutId);
        return response;
    } catch (error) {
        clearTimeout(timeoutId);
        throw error;
    }
}

/**
 * Fetches the VS Code extension manifest, trying the releases proxy first with fallback to releases.gitpod.io
 */
export async function fetchManifest(quality: 'latest' | 'stable'): Promise<FetchManifestResult> {
    const manifestPath = `vscode/${quality}/manifest.json`;
    
    // Try proxy first (preferred) with timeout
    const primaryUrl = `${RELEASES_PROXY}${manifestPath}`;
    try {
        console.log(`Trying primary URL: ${primaryUrl}`);
        const response = await fetchWithTimeout(primaryUrl, PRIMARY_TIMEOUT_MS);
        
        if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const manifest: GitpodVSCodeExtensionManifest = await response.json();
        const extensionUrl = validateAndExtractExtensionUrl(manifest);

        console.log(`Successfully fetched manifest from: ${primaryUrl}`);
        useProxy = true; // Primary (proxy) succeeded
        
        // Rewrite the extension URL to use the preferred base URL
        const rewrittenUrl = rewriteUrl(extensionUrl);
        return { extensionUrl: rewrittenUrl };
    } catch (error) {
        const errorMsg = error instanceof Error && error.name === 'AbortError' 
            ? 'timeout' 
            : error instanceof Error ? error.message : String(error);
        console.warn(`Primary URL failed (${errorMsg}), trying CDN fallback`);
    }

    // Fallback to releases.gitpod.io
    const fallbackUrl = `${RELEASES_GITPOD_IO}${manifestPath}`;
    console.log(`Trying fallback URL: ${fallbackUrl}`);
    const response = await fetch(fallbackUrl);
    
    if (!response.ok) {
        throw new Error(`Failed to fetch vscode extension manifest.json: ${response.statusText}`);
    }

    const manifest: GitpodVSCodeExtensionManifest = await response.json();
    const extensionUrl = validateAndExtractExtensionUrl(manifest);

    console.log(`Successfully fetched manifest from: ${fallbackUrl}`);
    useProxy = false; // Fallback (CDN) succeeded
    
    // Rewrite the extension URL to use the CDN
    const rewrittenUrl = rewriteUrl(extensionUrl);
    return { extensionUrl: rewrittenUrl };
}

/**
 * Rewrites a URL to use the appropriate base URL based on what worked during manifest fetch
 * Only rewrites releases-related URLs, returns other URLs unchanged
 */
export function rewriteUrl(url: string): string {
    // Only rewrite releases URLs
    if (!isReleasesUrl(url)) {
        return url;
    }
    
    // If we need to use proxy, convert releases.gitpod.io URLs to proxy
    if (useProxy && url.startsWith(RELEASES_GITPOD_IO)) {
        return url.replace(RELEASES_GITPOD_IO, RELEASES_PROXY);
    }
    
    return url;
}

/**
 * Checks if a URL is a releases-related URL that needs rewriting
 */
export function isReleasesUrl(url: string): boolean {
    return url.startsWith(RELEASES_GITPOD_IO) || 
           url.startsWith(RELEASES_PROXY);
}

/**
 * Fetches a resource using the preferred base URL determined during manifest fetch
 * If the URL is not a releases URL, fetches normally without rewriting
 */
export async function fetchWithPreferredBaseUrl(request: Request): Promise<Response> {
    const url = request.url;
    
    // Check if this is a releases-related URL
    if (!isReleasesUrl(url)) {
        // Not a releases URL, fetch normally
        return fetch(request);
    }
    
    // Rewrite URL to use the preferred base URL
    const targetUrl = rewriteUrl(url);
    
    if (targetUrl !== url) {
        console.log(`Rewrote URL: ${url} → ${targetUrl}`);
    }
    
    // Create new request with rewritten URL, preserving original request properties
    const targetRequest = new Request(targetUrl, request);
    return fetch(targetRequest);
}
